import { useLayoutEffect, useRef } from 'react';

const TOP_MARGIN = 16;

/**
 * Accepting or discarding a draft swaps the tall preview panel for the much
 * shorter editor. The page loses several hundred pixels under the user, the
 * browser clamps the scroll position, and the editor lands off screen above —
 * the user clicked a button and ended up somewhere they did not choose.
 *
 * Call `arm()` from the click that causes the swap: the editor is brought back
 * into view before the next paint, so the movement is never seen.
 */
export default function useKeepInView<T extends HTMLElement>(swapped: boolean) {
  const ref = useRef<T | null>(null);
  const armed = useRef(false);

  const arm = () => {
    armed.current = true;
  };

  useLayoutEffect(() => {
    const element = ref.current;
    if (!armed.current || !element) {
      return;
    }
    armed.current = false;

    // Only the upward jump is ours to undo. If the editor sits below the fold
    // the page did not move the user, and scrolling down uninvited would be a
    // second surprise on top of the first.
    const { top } = element.getBoundingClientRect();
    if (top >= 0) {
      return;
    }

    // Instant: this corrects a jump rather than travelling somewhere, and an
    // animation here would read as a second, unexplained movement.
    window.scrollBy({ top: top - TOP_MARGIN, behavior: 'auto' });
  }, [swapped]);

  return { ref, arm };
}
